<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { convertToAttributeSlug } from 'dashboard/helper/commons.js';
import { normalizeRegexPattern } from 'shared/helpers/Validators';
import { ATTRIBUTE_MODELS, IDS_WITH_OPTIONS } from './constants';

import NextButton from 'dashboard/components-next/button/Button.vue';
import OptionListEditor from './OptionListEditor.vue';

// Grupos principais exibidos na UI
const GROUP_COLORS = {
  text: {
    active: 'border-2 border-n-blue-6 text-n-blue-11 bg-n-blue-2',
    inactive:
      'border border-n-weak text-n-slate-11 hover:bg-n-blue-2 hover:border-n-blue-6 hover:text-n-blue-11',
  },
  number: {
    active: 'border-2 border-n-teal-6 text-n-teal-11 bg-n-teal-2',
    inactive:
      'border border-n-weak text-n-slate-11 hover:bg-n-teal-2 hover:border-n-teal-6 hover:text-n-teal-11',
  },
  list: {
    active: 'border-2 border-n-amber-6 text-n-amber-11 bg-n-amber-2',
    inactive:
      'border border-n-weak text-n-slate-11 hover:bg-n-amber-2 hover:border-n-amber-6 hover:text-n-amber-11',
  },
  checkbox: {
    active: 'border-2 border-n-orange-6 text-n-orange-11 bg-n-orange-2',
    inactive:
      'border border-n-weak text-n-slate-11 hover:bg-n-orange-2 hover:border-n-orange-6 hover:text-n-orange-11',
  },
  text_list: {
    active: 'border-2 border-n-gray-6 text-n-gray-11 bg-n-gray-2',
    inactive:
      'border border-n-weak text-n-slate-11 hover:bg-n-gray-2 hover:border-n-gray-6 hover:text-n-gray-11',
  },
  date: {
    active: 'border-2 border-n-violet-6 text-n-violet-11 bg-n-violet-2',
    inactive:
      'border border-n-weak text-n-slate-11 hover:bg-n-violet-2 hover:border-n-violet-6 hover:text-n-violet-11',
  },
  file: {
    active: 'border-2 border-n-ruby-6 text-n-ruby-11 bg-n-ruby-2',
    inactive:
      'border border-n-weak text-n-slate-11 hover:bg-n-ruby-2 hover:border-n-ruby-6 hover:text-n-ruby-11',
  },
};

const TYPE_GROUPS = [
  {
    key: 'text',
    label: 'Texto',
    defaultId: 0,
    suboptions: [
      { label: 'Texto longo', id: 8 },
      { label: 'E-mail', id: 15 },
      { label: 'Link', id: 4 },
    ],
  },
  {
    key: 'number',
    label: 'Número',
    defaultId: 1,
    suboptions: [
      { label: 'Texto longo', id: 8 },
      { label: 'Telefone', id: 10 },
      { label: 'Monetário', id: 2 },
      { label: 'Percentual', id: 3 },
      { label: 'CPF', id: 16 },
      { label: 'CNPJ', id: 17 },
    ],
  },
  {
    key: 'list',
    label: 'Lista',
    defaultId: 12,
    defaultLabel: 'Seleção múltipla',
    suboptions: [{ label: 'Seleção única', id: 6 }],
  },
  {
    key: 'checkbox',
    label: 'Caixa',
    defaultId: 13,
    defaultLabel: 'Seleção múltipla',
    suboptions: [{ label: 'Seleção única', id: 14 }],
  },
  {
    key: 'text_list',
    label: 'Campos',
    defaultId: 9,
    suboptions: [
      { label: 'Texto longo', id: 18 },
      { label: 'Somente números', id: 'numbers_only' },
    ],
  },
  {
    key: 'date',
    label: 'Data',
    defaultId: 5,
    suboptions: [],
  },
  {
    key: 'file',
    label: 'Arquivo',
    defaultId: 11,
    suboptions: [],
  },
];

export default {
  components: {
    NextButton,
    OptionListEditor,
  },
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    selectedAttributeModelTab: {
      type: Number,
      default: 0,
    },
    forcedFolderId: {
      type: Number,
      default: null,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      displayName: '',
      description: '',
      attributeModel: this.selectedAttributeModelTab || 0,
      attributeKey: '',
      halfWidth: false,
      values: [],
      show: true,
      tagInputTouched: false,
      selectedGroupKey: 'text',
      selectedSubId: null,
      modelDropdownOpen: false,
      subDropdownOpen: false,
    };
  },

  computed: {
    ...mapGetters({
      uiFlags: 'getUIFlags',
    }),
    models() {
      return ATTRIBUTE_MODELS.map(item => ({
        ...item,
        option: this.$t(`ATTRIBUTES_MGMT.ATTRIBUTE_MODELS.${item.key}`),
      }));
    },
    selectedModelLabel() {
      return this.models.find(m => m.id === this.attributeModel)?.option ?? '';
    },
    selectedSubLabel() {
      if (this.selectedSubId === null)
        return this.selectedGroup.defaultLabel ?? 'Padrão';
      return (
        this.selectedGroup.suboptions.find(s => s.id === this.selectedSubId)
          ?.label ?? 'Padrão'
      );
    },
    typeGroups() {
      return TYPE_GROUPS;
    },
    GROUP_COLORS() {
      return GROUP_COLORS;
    },
    selectedGroup() {
      return (
        TYPE_GROUPS.find(g => g.key === this.selectedGroupKey) || TYPE_GROUPS[0]
      );
    },
    // ID numérico enviado ao backend
    attributeType() {
      if (this.selectedSubId === null || this.selectedSubId === 'numbers_only')
        return this.selectedGroup.defaultId;
      return this.selectedSubId;
    },
    isNumbersOnly() {
      return this.selectedSubId === 'numbers_only';
    },
    isAttributeTypeWithOptions() {
      return IDS_WITH_OPTIONS.includes(this.attributeType);
    },
    isTagInputEmpty() {
      return (
        this.isAttributeTypeWithOptions &&
        this.values.filter(v => v.label && v.key).length === 0
      );
    },
    isTagInputInvalid() {
      return this.tagInputTouched && this.isTagInputEmpty;
    },
    isButtonDisabled() {
      return (
        this.v$.displayName.$invalid ||
        this.uiFlags.isCreating ||
        this.isTagInputEmpty
      );
    },
    keyErrorMessage() {
      if (!this.v$.attributeKey.isKey) {
        return this.$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.IN_VALID');
      }
      return this.$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.ERROR');
    },
  },

  watch: {
    selectedGroupKey() {
      this.selectedSubId = null;
      this.values = [];
      this.tagInputTouched = false;
    },
    selectedSubId() {
      this.values = [];
      this.tagInputTouched = false;
    },
  },

  mounted() {
    this.closeModelDropdownHandler = () => {
      this.modelDropdownOpen = false;
    };
    document.addEventListener('click', this.closeModelDropdownHandler);
    this.closeSubDropdownHandler = () => {
      this.subDropdownOpen = false;
    };
    document.addEventListener('click', this.closeSubDropdownHandler);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeModelDropdownHandler);
    document.removeEventListener('click', this.closeSubDropdownHandler);
  },

  methods: {
    onDisplayNameChange() {
      this.attributeKey = convertToAttributeSlug(this.displayName);
    },
    selectModel(id) {
      this.attributeModel = id;
      this.modelDropdownOpen = false;
    },
    selectSub(id) {
      this.selectedSubId = id;
      this.subDropdownOpen = false;
    },
    selectGroup(key) {
      this.selectedGroupKey = key;
    },
    toggleSub(id) {
      this.selectedSubId = this.selectedSubId === id ? null : id;
    },
    async addAttributes() {
      this.v$.$touch();
      if (this.v$.$invalid) return;

      try {
        await this.$store.dispatch('attributes/create', {
          attribute_display_name: this.displayName,
          attribute_description: this.description,
          attribute_model: this.attributeModel,
          attribute_display_type: this.attributeType,
          attribute_key: this.attributeKey,
          attribute_values: this.values,
          regex_pattern: this.isNumbersOnly
            ? normalizeRegexPattern('^\\d+$')
            : null,
          regex_cue: this.isNumbersOnly ? 'Apenas números' : null,
          kanban_folder_id: this.forcedFolderId,
          half_width: this.halfWidth,
        });
        this.alertMessage = this.$t('ATTRIBUTES_MGMT.ADD.API.SUCCESS_MESSAGE');
        this.onClose();
      } catch (error) {
        const errorMessage = error?.message;
        this.alertMessage =
          errorMessage || this.$t('ATTRIBUTES_MGMT.ADD.API.ERROR_MESSAGE');
      } finally {
        useAlert(this.alertMessage);
      }
    },
  },

  validations: {
    displayName: { required, minLength: minLength(1) },
    description: {},
    attributeModel: { required },
    attributeKey: {
      required,
      isKey(value) {
        return !(value.indexOf(' ') >= 0);
      },
    },
  },
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <woot-modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <form class="flex w-full" @submit.prevent="addAttributes">
        <div class="w-full flex flex-col gap-3 pt-2">
          <!-- Nome inline (título editável) -->
          <input
            v-model="displayName"
            type="text"
            :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            class="!text-base !font-semibold !text-n-slate-12 !bg-transparent !border-none !outline-none !shadow-none !ring-0 focus:!ring-0 focus:!border-none focus:!outline-none focus:!shadow-none !mb-0 !h-auto w-full placeholder:!text-n-slate-9 placeholder:!font-normal !p-0 !rounded-none"
            @input="onDisplayNameChange"
            @blur="v$.displayName.$touch"
          />
          <span
            v-if="v$.displayName.$error"
            class="text-xs text-n-ruby-9 -mt-2"
          >
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.NAME.ERROR') }}
          </span>

          <!-- Aplicação + Chave lado a lado -->
          <div class="flex items-start gap-3">
            <!-- Aplicação -->
            <div class="flex flex-col gap-0.5 flex-1">
              <span
                class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide"
              >
                {{ $t('ATTRIBUTES_MGMT.ADD.FORM.MODEL.LABEL') }}
              </span>
              <div class="relative" @click.stop>
                <button
                  type="button"
                  class="flex items-center justify-between w-full gap-1 bg-n-alpha-1 hover:bg-n-alpha-2 rounded px-2 py-1 min-h-[30px] text-sm text-n-slate-12 transition-colors"
                  @click="modelDropdownOpen = !modelDropdownOpen"
                >
                  <span>{{ selectedModelLabel }}</span>
                  <span
                    class="i-lucide-chevron-down size-3 text-n-slate-9 flex-shrink-0"
                  />
                </button>
                <div
                  v-if="modelDropdownOpen"
                  class="absolute left-0 top-full mt-1 z-50 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 min-w-full"
                >
                  <button
                    v-for="model in models"
                    :key="model.id"
                    type="button"
                    class="flex items-center w-full px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
                    :class="
                      model.id === attributeModel
                        ? 'text-n-brand font-medium'
                        : 'text-n-slate-12'
                    "
                    @click="selectModel(model.id)"
                  >
                    {{ model.option }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Chave -->
            <div class="flex flex-col gap-0.5 flex-1">
              <span
                class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide"
              >
                {{ $t('ATTRIBUTES_MGMT.ADD.FORM.KEY.LABEL') }}
              </span>
              <input
                v-model="attributeKey"
                type="text"
                :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.PLACEHOLDER')"
                class="!bg-n-alpha-1 hover:!bg-n-alpha-2 focus:!bg-n-alpha-2 !rounded !px-2 !py-1 !h-auto !min-h-[30px] !text-sm !text-n-slate-12 placeholder:!text-n-slate-9 !outline-none !border-0 !shadow-none !ring-0 focus:!ring-0 !mb-0 !transition-colors w-full"
                :class="
                  v$.attributeKey.$error
                    ? '!outline !outline-1 !outline-n-ruby-6'
                    : ''
                "
                @blur="v$.attributeKey.$touch"
              />
              <span v-if="v$.attributeKey.$error" class="text-xs text-n-ruby-9">
                {{ keyErrorMessage }}
              </span>
            </div>
          </div>

          <!-- Descrição -->
          <div class="flex flex-col gap-0.5">
            <span
              class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide"
            >
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.LABEL') }}
            </span>
            <div
              contenteditable="true"
              :data-placeholder="
                $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.PLACEHOLDER')
              "
              class="text-sm px-2 py-1.5 rounded bg-n-alpha-1 hover:bg-n-alpha-2 focus:bg-n-alpha-2 text-n-slate-12 min-h-[30px] w-full outline-none overflow-hidden transition-colors [word-break:break-word] [overflow-wrap:break-word] [white-space:pre-wrap] empty:before:content-[attr(data-placeholder)] empty:before:text-n-slate-9"
              @input="description = $event.target.innerText"
              @blur="description = $event.target.innerText"
            />
          </div>

          <!-- Seletor de tipo principal -->
          <div class="flex flex-col gap-1.5">
            <span
              class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide"
            >
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LABEL') }}
            </span>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="group in typeGroups"
                :key="group.key"
                type="button"
                class="px-3 py-1.5 text-sm rounded-lg transition-colors"
                :class="
                  selectedGroupKey === group.key
                    ? GROUP_COLORS[group.key].active
                    : GROUP_COLORS[group.key].inactive
                "
                @click="selectGroup(group.key)"
              >
                {{ group.label }}
              </button>
            </div>
          </div>

          <!-- Opções de lista -->
          <div v-if="isAttributeTypeWithOptions" class="flex flex-col gap-1.5">
            <div
              class="rounded-xl border py-2"
              :class="isTagInputInvalid ? 'border-n-ruby-9' : 'border-n-weak'"
            >
              <OptionListEditor
                v-model="values"
                :show-placeholder="attributeType === 9 || attributeType === 18"
                @blur="tagInputTouched = true"
              />
            </div>
            <span v-show="isTagInputInvalid" class="text-n-ruby-9 text-xs">
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.ERROR') }}
            </span>
          </div>

          <!-- Personalizar + Proporção 50% + Salvar -->
          <div class="flex items-end gap-4">
            <div
              v-if="selectedGroup.suboptions.length"
              class="flex flex-col gap-0.5 w-2/5"
            >
              <span
                class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide"
              >
                Personalizar
              </span>
              <div class="relative" @click.stop>
                <button
                  type="button"
                  class="flex items-center justify-between w-full gap-1 bg-n-alpha-1 hover:bg-n-alpha-2 rounded px-2 py-1 min-h-[30px] text-sm text-n-slate-12 transition-colors"
                  @click="subDropdownOpen = !subDropdownOpen"
                >
                  <span>{{ selectedSubLabel }}</span>
                  <span
                    class="i-lucide-chevron-down size-3 text-n-slate-9 flex-shrink-0"
                  />
                </button>
                <div
                  v-if="subDropdownOpen"
                  class="absolute left-0 bottom-full mb-1 z-50 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 min-w-full"
                >
                  <button
                    type="button"
                    class="flex items-center w-full px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
                    :class="
                      selectedSubId === null
                        ? 'text-n-brand font-medium'
                        : 'text-n-slate-12'
                    "
                    @click="selectSub(null)"
                  >
                    {{ selectedGroup.defaultLabel ?? 'Padrão' }}
                  </button>
                  <button
                    v-for="sub in selectedGroup.suboptions"
                    :key="sub.id"
                    type="button"
                    class="flex items-center w-full px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
                    :class="
                      selectedSubId === sub.id
                        ? 'text-n-brand font-medium'
                        : 'text-n-slate-12'
                    "
                    @click="selectSub(sub.id)"
                  >
                    {{ sub.label }}
                  </button>
                </div>
              </div>
            </div>

            <button
              type="button"
              class="flex items-center gap-2 shrink-0"
              @click="halfWidth = !halfWidth"
            >
              <span class="text-sm font-medium text-n-slate-11"
                >Proporção 50%</span
              >
              <div
                class="relative w-9 h-5 rounded-full transition-colors duration-200"
                :class="halfWidth ? 'bg-n-brand' : 'bg-n-slate-5'"
              >
                <div
                  class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200"
                  :class="halfWidth ? 'translate-x-4' : 'translate-x-0'"
                />
              </div>
            </button>

            <NextButton
              type="submit"
              label="Salvar"
              class="ml-auto !h-[30px] !text-xs !px-3"
              :disabled="isButtonDisabled"
            />
          </div>
        </div>
      </form>
    </div>
  </woot-modal>
</template>
